/* eslint-disable max-lines-per-function */
import {
  cancelFirstPageLoadMeasure,
  getErrorMessage,
  getErrorName,
  reportFirstPageLoad,
  requestAnimationFrameAfterStyleAndLayout,
} from '@ms/yammer-telemetry';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { getHeroStringForTelemetry, hasHeroMatch } from '../../../telemetry/hero';
import { getPageLoadId } from '../../../telemetry/pageLoadId';
import {
  cancelPageLoadTimingEvent,
  clearCachedAppPageLoadTracking,
  endAndReportCachedAppPageLoadTimingEventAfterStyleAndLayout,
  endAndReportPageLoadTimingEventAfterStyleAndLayout,
  isCachedAppPageLoad,
  startOrUpdatePageLoadTiming,
  updatePageLoadTimingEvent,
} from '../../../telemetry/pageLoadTiming';
import type {
  PageTelemetryContextValue,
  ReportHeroComponentRendered,
  ReportHeroComponentRenderedExpectedTerminalErrorOptions,
  ReportHeroComponentRenderedRecoverableErrorOptions,
} from '../context';

import { useDidVisibilityStateChange } from './useDidVisibilityStateChange';

export type UsePageLoadTelemetryCallbackOptions = Pick<
  PageTelemetryContextValue,
  'page' | 'path' | 'hero' | 'isRoutelessApp'
> & { readonly pathname?: string; readonly pageLoadId: number };
type UsePageLoadTelemetryCallback = (options: UsePageLoadTelemetryCallbackOptions) => ReportHeroComponentRendered;

/**
 * Reports FirstPageLoad, route-to-route PageLoad, and CachedFirstPageLoad times, handling abandon/cancel and timeout events.
 * Returns a callback to be called by any hero component which manages reporting after the appropriate browser paint delay.
 */
export const usePageLoadTelemetryCallback: UsePageLoadTelemetryCallback = ({
  pathname: pathnameProp,
  pageLoadId,
  path,
  page,
  hero,
  isRoutelessApp,
}) => {
  const { pathname: urlPathname } = useLocation();
  const pathname = pathnameProp || urlPathname;

  startOrUpdatePageLoadTiming({
    pageLoadId,
    eventProperties: {
      page,
      path,
      hero: getHeroStringForTelemetry(hero),
      urlPath: pathname,
    },
  });

  const { didVisibilityStateChange, didVisibilityStateChangeRef } = useDidVisibilityStateChange({
    key: `${page}-${pageLoadId}`,
  });

  useEffect(
    function updatePendingPageLoadEventOnVisibilityChanges() {
      if (didVisibilityStateChange) {
        updatePageLoadTimingEvent({
          pageLoadId,
          eventProperties: { page, path, hero: getHeroStringForTelemetry(hero), didVisibilityStateChange },
        });
      }
    },
    [didVisibilityStateChange, hero, page, pageLoadId, path]
  );

  useEffect(() => {
    const didVisibilityStateChangeRefValue = didVisibilityStateChangeRef.current;

    return function cancelPendingPageLoadEventsOnRouteChange() {
      const hasRouteChanged = pageLoadId !== getPageLoadId();
      if (!hasRouteChanged) {
        return;
      }

      cancelFirstPageLoadMeasure({
        eventProperties: {
          page,
          path,
          hero: getHeroStringForTelemetry(hero),
          urlPath: pathname,
          didVisibilityStateChange: didVisibilityStateChangeRefValue,
        },
      });

      if (!isCachedAppPageLoad({ isRoutelessApp })) {
        cancelPageLoadTimingEvent({
          pageLoadId,
          eventProperties: {
            didVisibilityStateChange: didVisibilityStateChangeRefValue,
          },
        });
      }
    };
  }, [didVisibilityStateChangeRef, hero, isRoutelessApp, page, pageLoadId, path, pathname]);

  return useCallback(
    function reportHeroRendered(component, heroOptions = {}) {
      if (!hasHeroMatch(component, hero)) {
        return;
      }

      const isCachedLoad = isCachedAppPageLoad({ isRoutelessApp });
      const endedWithError = heroOptions.endedWithError;
      const recoverableErrorOptions = heroOptions as ReportHeroComponentRenderedRecoverableErrorOptions &
        ReportHeroComponentRenderedExpectedTerminalErrorOptions;
      const isExpectedTerminalState = recoverableErrorOptions.isExpectedTerminalState;
      const isExpectedError = recoverableErrorOptions.isRecoverable || isExpectedTerminalState;
      const errorCode = recoverableErrorOptions.error ? getErrorName(recoverableErrorOptions.error) : undefined;
      const errorMessage = getErrorMessage(recoverableErrorOptions.error);

      endAndReportPageLoadTimingEventAfterStyleAndLayout({
        pageLoadId,
        eventProperties: {
          didVisibilityStateChange: didVisibilityStateChangeRef.current,
          hero: component,
          endedWithError,
          isExpectedError,
          isExpectedTerminalState,
          errorMessage,
          errorCode,
        },
      });

      if (isCachedLoad) {
        endAndReportCachedAppPageLoadTimingEventAfterStyleAndLayout({
          eventProperties: {
            page,
            path,
            urlPath: pathname,
            hero: component,
            deepLink: true,
            didVisibilityStateChange: didVisibilityStateChangeRef.current,
            endedWithError,
            isExpectedError,
            isExpectedTerminalState,
            errorMessage,
            errorCode,
          },
        });
        clearCachedAppPageLoadTracking({ isRoutelessApp });
      }

      requestAnimationFrameAfterStyleAndLayout(() => {
        reportFirstPageLoad({
          eventProperties: {
            page,
            path,
            hero: component,
            urlPath: pathname,
            didVisibilityStateChange: didVisibilityStateChangeRef.current,
            endedWithError,
            isExpectedError,
            isExpectedTerminalState,
            errorMessage,
            errorCode,
          },
        });
      });
    },
    [hero, pageLoadId, didVisibilityStateChangeRef, isRoutelessApp, page, path, pathname]
  );
};
